import { useState } from 'react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  AdminPayment,
  AdminPaymentFilter,
  useAllPayments,
  useApprovePayment,
  useRejectPayment,
} from '@/hooks/useAdminPayments';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import { Check, X, FileText, Receipt } from 'lucide-react';

const STATUS_FILTERS: { value: AdminPaymentFilter; label: string }[] = [
  { value: 'pending', label: 'Pendientes' },
  { value: 'approved', label: 'Aprobados' },
  { value: 'rejected', label: 'Rechazados' },
  { value: 'refunded', label: 'Reembolsados' },
  { value: 'all', label: 'Todos' },
];

const STATUS_BADGE: Record<string, { label: string; className: string }> = {
  pending: { label: 'Pendiente', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Aprobado', className: 'bg-green-100 text-green-800' },
  rejected: { label: 'Rechazado', className: 'bg-red-100 text-red-800' },
  refunded: { label: 'Reembolsado', className: 'bg-slate-100 text-slate-800' },
};

const coupleName = (p: AdminPayment): string => {
  if (p.groom_name || p.bride_name) {
    return [p.groom_name, p.bride_name].filter(Boolean).join(' & ');
  }
  return 'Sin invitación vinculada';
};

const isPdf = (url: string): boolean =>
  /\.pdf($|\?)/i.test(url) || /application\/pdf/i.test(url);

const fmtMoney = (n: number): string =>
  n.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const AdminPayments = () => {
  const [filter, setFilter] = useState<AdminPaymentFilter>('pending');
  const { data: payments, isLoading, error } = useAllPayments(filter);
  const approvePayment = useApprovePayment();
  const rejectPayment = useRejectPayment();
  const { toast } = useToast();

  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminPayment | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const handleApprove = async (payment: AdminPayment) => {
    try {
      await approvePayment.mutateAsync(payment.id);
      toast({
        title: 'Pago aprobado',
        description: `La invitación de ${coupleName(payment)} quedó habilitada para publicar.`,
      });
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo aprobar el pago.',
        variant: 'destructive',
      });
    }
  };

  const handleReject = async () => {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (!reason) {
      toast({
        title: 'Falta la razón',
        description: 'Escribe el motivo del rechazo para que la pareja lo entienda.',
        variant: 'destructive',
      });
      return;
    }
    try {
      await rejectPayment.mutateAsync({ paymentId: rejectTarget.id, reason });
      toast({
        title: 'Pago rechazado',
        description: `Se notificó el rechazo a ${coupleName(rejectTarget)}.`,
      });
      setRejectTarget(null);
      setRejectReason('');
    } catch (e) {
      toast({
        title: 'Error',
        description: e instanceof Error ? e.message : 'No se pudo rechazar el pago.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-muted-foreground">
          {payments ? `${payments.length} pago(s)` : ' '}
        </p>
        <Select value={filter} onValueChange={(v) => setFilter(v as AdminPaymentFilter)}>
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((f) => (
              <SelectItem key={f.value} value={f.value}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : error ? (
        <p className="text-destructive py-6 text-center">Error al cargar los pagos.</p>
      ) : !payments || payments.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Receipt className="mx-auto h-12 w-12 mb-3 opacity-50" />
          <p>No hay pagos en este estado.</p>
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Pareja</TableHead>
                <TableHead className="text-right">Bs</TableHead>
                <TableHead className="text-right">USD</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead>Referencia</TableHead>
                <TableHead>Notas</TableHead>
                <TableHead>Comprobante</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => {
                const badge = STATUS_BADGE[p.status] ?? {
                  label: p.status,
                  className: 'bg-muted text-foreground',
                };
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium">{coupleName(p)}</div>
                      {p.payer_email && (
                        <a
                          href={`mailto:${p.payer_email}`}
                          className="text-xs text-muted-foreground hover:text-primary"
                        >
                          {p.payer_email}
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(p.amount_bs)}</TableCell>
                    <TableCell className="text-right tabular-nums">{fmtMoney(p.amount_usd)}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {format(new Date(p.created_at), "d MMM yyyy, HH:mm", { locale: es })}
                    </TableCell>
                    <TableCell className="text-sm">{p.bank_reference || '—'}</TableCell>
                    <TableCell className="max-w-[16rem] text-sm">
                      {p.notes ? (
                        <span className="line-clamp-2">{p.notes}</span>
                      ) : (
                        '—'
                      )}
                      {p.status === 'rejected' && p.rejection_reason && (
                        <span className="mt-1 block text-xs text-red-600">
                          Rechazo: {p.rejection_reason}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {p.receiptSignedUrl ? (
                        <button
                          type="button"
                          onClick={() => setReceiptUrl(p.receiptSignedUrl)}
                          className="block"
                          title="Ver comprobante"
                        >
                          {isPdf(p.receiptSignedUrl) ? (
                            <span className="flex h-12 w-12 items-center justify-center rounded border bg-muted">
                              <FileText className="h-5 w-5 text-muted-foreground" />
                            </span>
                          ) : (
                            <img
                              src={p.receiptSignedUrl}
                              alt="Comprobante"
                              className="h-12 w-12 rounded border object-cover transition-opacity hover:opacity-80"
                            />
                          )}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">Sin comprobante</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={badge.className}>
                        {badge.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {p.status === 'pending' ? (
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(p)}
                            disabled={approvePayment.isPending}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setRejectTarget(p);
                              setRejectReason('');
                            }}
                            disabled={rejectPayment.isPending}
                          >
                            <X className="h-4 w-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {p.reviewed_at
                            ? format(new Date(p.reviewed_at), "d MMM yyyy", { locale: es })
                            : '—'}
                        </span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Receipt zoom modal */}
      <Dialog open={!!receiptUrl} onOpenChange={(open) => !open && setReceiptUrl(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Comprobante de pago</DialogTitle>
          </DialogHeader>
          {receiptUrl &&
            (isPdf(receiptUrl) ? (
              <iframe src={receiptUrl} title="Comprobante" className="h-[70vh] w-full rounded border" />
            ) : (
              <img
                src={receiptUrl}
                alt="Comprobante"
                className="max-h-[70vh] w-full rounded border object-contain"
              />
            ))}
        </DialogContent>
      </Dialog>

      {/* Reject with reason modal */}
      <Dialog
        open={!!rejectTarget}
        onOpenChange={(open) => {
          if (!open) {
            setRejectTarget(null);
            setRejectReason('');
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar pago</DialogTitle>
            <DialogDescription>
              {rejectTarget && (
                <>Indica el motivo del rechazo para {coupleName(rejectTarget)}. La pareja lo verá en su panel.</>
              )}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Ej: el monto transferido no coincide con el comprobante."
            rows={4}
            autoFocus
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectTarget(null);
                setRejectReason('');
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectPayment.isPending}
            >
              Confirmar rechazo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPayments;
